/**
 * Diálogo de cambio de rol de un miembro (AUTONOMY · Layer 1).
 *
 * Roles de ACADEMIA (no los legados viewer/member): cada rol se muestra con su
 * descripción de efectos (Layer 2: auto-descriptivo). `owner` solo aparece si
 * el actor es owner (el backend además exige owner para tocar owners y protege
 * al último owner).
 */
import { createSignal, For, Show } from "solid-js";
import { Dialog, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Check, Shield } from "lucide-solid";
import type { MessageDescriptor } from "@lingui/core";
import { Button } from "@/components/ui/button";
import { cn, type OrganizationRole } from "@space/shared";
import { useI18n } from "@/providers/i18n-provider";
import { msg } from "@lingui/core/macro";

interface ChangeRoleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  memberName: string;
  currentRole: string;
  /** ¿El actor es owner? Habilita ofrecer el rol owner (transferencia). */
  actorIsOwner?: boolean;
  onRoleChange: (role: OrganizationRole) => Promise<void>;
}

interface RoleOption {
  id: OrganizationRole;
  label: MessageDescriptor;
  description: MessageDescriptor;
}

const ACADEMY_ROLE_OPTIONS: RoleOption[] = [
  {
    id: "org_admin",
    label: msg`Organization admin`,
    description: msg`Manages the whole academy: settings, people, cohorts, billing.`,
  },
  {
    id: "campus_admin",
    label: msg`Campus admin`,
    description: msg`Manages a single campus: its cohorts, schedules and people.`,
  },
  {
    id: "teacher",
    label: msg`Teacher`,
    description: msg`Teaches cohorts: attendance, gradebook, assignments, forum.`,
  },
  {
    id: "staff",
    label: msg`Staff`,
    description: msg`Front desk and cashier: records payments and manages leads. Cannot verify payments.`,
  },
  {
    id: "student",
    label: msg`Student`,
    description: msg`Learns in enrolled cohorts and pays tuition.`,
  },
  {
    id: "guardian",
    label: msg`Guardian`,
    description: msg`Follows the progress, attendance and debt of linked students.`,
  },
];

const OWNER_OPTION: RoleOption = {
  id: "owner",
  label: msg`Owner`,
  description: msg`Organization owner: full control, can transfer ownership or delete the academy.`,
};

export function ChangeRoleDialog(props: ChangeRoleDialogProps) {
  const { t } = useI18n();
  const [loading, setLoading] = createSignal(false);

  const roles = (): RoleOption[] =>
    props.actorIsOwner ? [...ACADEMY_ROLE_OPTIONS, OWNER_OPTION] : ACADEMY_ROLE_OPTIONS;

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange} contentClass="max-w-md p-0">
      <DialogHeader size="md">
        <div class="flex items-center gap-3">
          <div class="h-10 w-10 rounded-xl bg-amber-500/10 flex items-center justify-center text-amber-500">
            <Shield class="h-5 w-5" />
          </div>
          <div>
            <DialogTitle class="text-xl font-bold">{t(msg`Change Role`)}</DialogTitle>
            <DialogDescription class="text-xs text-muted-foreground">
              {t(msg`Define permissions for {{name}}`, { name: props.memberName })}
            </DialogDescription>
          </div>
        </div>
      </DialogHeader>

      <div class="p-2 space-y-1 max-h-[60vh] overflow-y-auto">
        <For each={roles()}>
          {(role) => (
            <button
              type="button"
              disabled={loading()}
              data-testid={`change-role-${role.id}`}
              // oxlint-disable-next-line typescript-eslint/promise-function-async
              onClick={() => {
                setLoading(true);
                void (async () => {
                  try {
                    await props.onRoleChange(role.id);
                  } finally {
                    setLoading(false);
                    props.onOpenChange(false);
                  }
                })();
              }}
              class={cn(
                "w-full flex items-center justify-between p-4 rounded-xl hover:bg-accent transition-all text-left group cursor-pointer",
                props.currentRole === role.id && "bg-primary/5 border border-primary/20",
              )}
            >
              <div class="flex-1">
                <div class="font-bold flex items-center gap-2">
                  {t(role.label)}
                  <Show when={props.currentRole === role.id}>
                    <span class="text-[10px] bg-primary/20 text-primary px-1.5 py-0.5 rounded uppercase tracking-wider">
                      {t(msg`Current`)}
                    </span>
                  </Show>
                </div>
                <div class="text-xs text-muted-foreground">{t(role.description)}</div>
              </div>
              <Show when={props.currentRole === role.id}>
                <Check class="h-5 w-5 text-primary" />
              </Show>
            </button>
          )}
        </For>
      </div>

      <div class="p-4 border-t border-border/10 bg-muted/20">
        <Button
          variant="ghost"
          class="w-full h-11 font-bold rounded-xl"
          onClick={() => {
            props.onOpenChange(false);
          }}
        >
          {t(msg`Cancel`)}
        </Button>
      </div>
    </Dialog>
  );
}
